import { formatNumber } from '@platform/design-system';
import { LoadMore } from './LoadMore.tsx';
import { ResourceTable, type ResourceQuery, type ResourceTableProps } from './ResourceTable.tsx';
import { usePageContext } from './usePageContext.ts';

export type KeysetRows<Row> = {
  readonly query: ResourceQuery;
  readonly items: readonly Row[];
  readonly total: number;
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
  readonly loadMore: () => void;
};

export type ShownLabel = (shown: string, total: string, count: number) => string;

export type KeysetTableProps<Row> = Omit<ResourceTableProps<Row>, 'query' | 'rows' | 'footer'> & {
  pages: KeysetRows<Row>;
  shownLabel?: ShownLabel;
};

type KeysetLoadMoreProps<Row> = { pages: KeysetRows<Row>; shownLabel?: ShownLabel | undefined };

const KeysetLoadMore = <Row,>({ pages, shownLabel }: KeysetLoadMoreProps<Row>) => {
  const { t, locale } = usePageContext();
  const shown = formatNumber(pages.items.length, locale);
  const total = formatNumber(pages.total, locale);
  return (
    <LoadMore
      shownLabel={
        shownLabel === undefined ? t('common.shown', { shown, total }) : shownLabel(shown, total, pages.total)
      }
      hasMore={pages.hasMore}
      loading={pages.loadingMore}
      onLoadMore={pages.loadMore}
    />
  );
};

export const KeysetTable = <Row,>({ pages, shownLabel, ...table }: KeysetTableProps<Row>) => (
  <ResourceTable
    {...table}
    query={pages.query}
    rows={pages.items}
    footer={pages.items.length === 0 ? undefined : <KeysetLoadMore pages={pages} shownLabel={shownLabel} />}
  />
);
